import {
  FormControl,
  InputLabel,
  makeStyles,
  MenuItem,
  Select,
  Typography
} from "@material-ui/core";
import React, { useEffect, useRef, useState } from "react";
import styles from "assets/jss/material-dashboard-pro-react/views/formsStyle.js";
import GridContainer from "components/Grid/GridContainer";
import GridItem from "components/Grid/GridItem";
import Card from "components/Card/Card";
import CardHeader from "components/Card/CardHeader";
import CardIcon from "components/Card/CardIcon";
import MailOutline from "@material-ui/icons/MailOutline";
import CardBody from "components/Card/CardBody";
import Button from "components/CustomButtons/Button.js";
import PropTypes from "prop-types";
import CustomInput from "components/CustomInput/CustomInput";
import { travelApi } from "functions/apis";
import { useParams, Redirect } from "react-router-dom";
import { formatCurrency } from "functions/Helper";
import { formatDateTime } from "functions/Helper";
import ViewMerchantOnlineBookingDetail from "./ViewMerchantOnlineBookingDetail";

const useStyles = makeStyles(styles);

const statusList = ["Pending", "Submitted"];
const detailStatusList = ["Pending", "Confirmed", "Amended", "Rejected"];

const CompleteMerchantOnlineBooking = props => {
  const classes = useStyles();

  const [input, setInput] = useState(null);
  const [redirect, setRedirect] = useState(false);
  const [disabled, setDisabled] = useState(false);

  const [payload, setPayload] = useState({
    details: [],
    remarks: ""
  });

  const { id } = useParams();

  const completedMerchantOnlineBookingSignal = useRef(new AbortController());

  useEffect(() => {
    travelApi
      .get("OnlineBookings/" + id, {
        signal: completedMerchantOnlineBookingSignal.current.signal
      })
      .then(resp => {
        setInput(resp);
        if (resp?.details?.length > 0) {
          const details = resp.details.map(detail => ({
            bookingDetailID: detail.id,
            status: detail.status,
            remarks: detail.remarks ?? "",
            confirmationCode: detail.confirmationCode ?? ""
          }));
          setPayload({ ...payload, details });
        }
      });
    return () => completedMerchantOnlineBookingSignal.current.abort();
  }, []);

  const handleStatusChanged = (e, index) => {
    setPayload({
      ...payload,
      details: payload.details.map((val, key) => {
        if (key === index) {
          return {
            ...val,
            status: e.target.value === "" ? null : e.target.value
          };
        }
        return val;
      })
    });
  };

  const handleConfirmationCodeChanged = (e, index) => {
    setPayload({
      ...payload,
      details: payload.details.map((val, key) => {
        if (key === index) {
          return {
            ...val,
            confirmationCode: e.target.value === "" ? null : e.target.value
          };
        }
        return val;
      })
    });
  };

  const handleRemarksPayloadChanged = e => {
    setPayload({
      ...payload,
      remarks: e.target.value === "" ? null : e.target.value
    });
  };

  const handleRemarksChanged = (e, index) => {
    setPayload({
      ...payload,
      details: payload.details.map((val, key) => {
        if (key === index) {
          return {
            ...val,
            remarks: e.target.value === "" ? null : e.target.value
          };
        }
        return val;
      })
    });
  };

  const handleCompletePressed = e => {
    e.preventDefault();
    setDisabled(true);
    travelApi
      .post("OnlineBookings/" + id + "/SetAsCompleted", payload, {
        signal: completedMerchantOnlineBookingSignal.current.signal
      })
      .then(() => {
        setRedirect(true);
      })
      .finally(() => {
        setDisabled(false);
      });
  };

  return redirect ? (
    <Redirect to={"/admin/MerchantOnlineBooking/CompletedList"} />
  ) : (
    <GridContainer>
      <GridItem xs={12}>
        <Card>
          <CardHeader color="rose" icon>
            <CardIcon color="rose">
              <MailOutline />
            </CardIcon>
            <h4 className={classes.cardIconTitle}>
              COMPLETED MERCHANT ONLINE BOOKING
            </h4>
          </CardHeader>
          <CardBody>
            <i>
              *Are you sure you want to change the status of this online
              merchant booking to complete?
            </i>
            <form onSubmit={handleCompletePressed}>
              <Button onClick={() => props.history.goBack()} value="Cancel">
                CANCEL
              </Button>
            </form>
            {input != null && (
              <form>
                <GridContainer>
                  <GridItem xs={6}>
                    <CustomInput
                      labelText="Booking No"
                      formControlProps={{
                        fullWidth: true
                      }}
                      inputProps={{
                        value: input.no,
                        disabled: true
                      }}
                    />
                  </GridItem>
                  <GridItem xs={6}>
                    <CustomInput
                      labelText="Status"
                      formControlProps={{
                        fullWidth: true
                      }}
                      inputProps={{
                        value: statusList[input.status],
                        disabled: true
                      }}
                    />
                  </GridItem>
                  {input.status == 1 && (
                    <GridItem xs={6}>
                      <CustomInput
                        labelText="Submitted At"
                        formControlProps={{
                          fullWidth: true
                        }}
                        inputProps={{
                          value: formatDateTime(
                            new Date(input.submittedAt + "Z")
                          ),
                          disabled: true
                        }}
                      />
                    </GridItem>
                  )}
                  {input.status == 1 && (
                    <GridItem xs={6}>
                      <CustomInput
                        labelText="Submitted By"
                        formControlProps={{
                          fullWidth: true
                        }}
                        inputProps={{
                          value: input.submittedBy,
                          disabled: true
                        }}
                      />
                    </GridItem>
                  )}
                  <GridItem xs={6}>
                    <CustomInput
                      labelText="Email Confirmation"
                      formControlProps={{
                        fullWidth: true
                      }}
                      inputProps={{
                        value: input.emailConfirmation,
                        disabled: true
                      }}
                    />
                  </GridItem>
                  <GridItem xs={6}>
                    <CustomInput
                      labelText="Transaction Time"
                      formControlProps={{
                        fullWidth: true
                      }}
                      inputProps={{
                        value: formatDateTime(
                          new Date(input.transactionTime + "Z")
                        ),
                        disabled: true
                      }}
                    />
                  </GridItem>
                  <GridItem xs={6}>
                    <CustomInput
                      labelText="Total"
                      formControlProps={{
                        fullWidth: true
                      }}
                      inputProps={{
                        value:
                          input == null
                            ? ""
                            : formatCurrency(
                                input.currency === null
                                  ? "XXX"
                                  : input.currency.code,
                                input.total
                              ),
                        disabled: true
                      }}
                    />
                  </GridItem>
                  <GridItem xs={6}>
                    <CustomInput
                      labelText="Remarks"
                      formControlProps={{
                        fullWidth: true
                      }}
                      inputProps={{
                        value: input.remarks,
                        disabled: true
                      }}
                    />
                  </GridItem>
                </GridContainer>
              </form>
            )}
          </CardBody>
        </Card>
        {input &&
          input.details.length > 0 &&
          input.details.map((val, key) => (
            <ViewMerchantOnlineBookingDetail
              key={key}
              data={val}
              currency={input.currency.code}
            />
          ))}
        <Card>
          <CardHeader color="rose" icon>
            <CardIcon color="rose">
              <MailOutline />
            </CardIcon>
            <h4 className={classes.cardIconTitle}>
              SET AS COMPLETE MERCHANT ONLINE BOOKING
            </h4>
          </CardHeader>
          <CardBody>
            <GridContainer>
              <GridItem xs={6}>
                <CustomInput
                  labelText="Remarks"
                  formControlProps={{
                    fullWidth: true
                  }}
                  inputProps={{
                    onChange: handleRemarksPayloadChanged,
                    value: payload.remarks
                  }}
                />
              </GridItem>
            </GridContainer>
            {input &&
              input.details.length > 0 &&
              payload.details.length > 0 &&
              input.details.map((val, key) => {
                console.log(
                  !payload ||
                    (payload && !payload.details[key]) ||
                    (payload &&
                      payload.details[key] &&
                      (payload.details[key].status === 0 ||
                        payload.details[key].status === 3))
                );
                return (
                  <GridContainer key={key}>
                    <GridItem xs={12}>
                      <Typography>{`${val.product.name} (${val.variant.name})`}</Typography>
                    </GridItem>
                    <GridItem xs={12}>
                      <FormControl
                        fullWidth
                        className={classes.selectFormControl}
                      >
                        <InputLabel
                          htmlFor="selSortOptions"
                          className={classes.selectLabel}
                        >
                          Status
                        </InputLabel>
                        <Select
                          inputProps={{
                            id: "selSortOptions",
                            name: "selSortOptions"
                          }}
                          MenuProps={{
                            className: classes.selectMenu
                          }}
                          classes={{
                            select: classes.select
                          }}
                          value={
                            payload.details[key]
                              ? payload.details[key].status
                              : ""
                          }
                          onChange={e => handleStatusChanged(e, key)}
                        >
                          {detailStatusList.map((record, key) => {
                            return (
                              <MenuItem
                                classes={{
                                  root: classes.selectMenuItem,
                                  selected: classes.selectMenuItemSelected
                                }}
                                value={key}
                                key={record}
                              >
                                <p>{record}</p>
                              </MenuItem>
                            );
                          })}
                        </Select>
                      </FormControl>
                    </GridItem>
                    <GridItem xs={6}>
                      <CustomInput
                        labelText="Confirmation Code"
                        formControlProps={{
                          fullWidth: true
                        }}
                        inputProps={{
                          onChange: e => handleConfirmationCodeChanged(e, key),
                          value: payload.details[key].confirmationCode
                            ? payload.details[key].confirmationCode
                            : "",
                          disabled:
                            !payload ||
                            (payload && !payload.details[key]) ||
                            (payload &&
                              payload.details[key] &&
                              (payload.details[key].status === 0 ||
                                payload.details[key].status === 3))
                        }}
                      />
                    </GridItem>
                    <GridItem xs={6}>
                      <CustomInput
                        labelText="Remarks"
                        formControlProps={{
                          fullWidth: true
                        }}
                        inputProps={{
                          onChange: e => handleRemarksChanged(e, key),
                          value: payload.details[key]
                            ? payload.details[key].remarks
                            : ""
                        }}
                      />
                    </GridItem>
                  </GridContainer>
                );
              })}
            <GridContainer direction="row" sx={{ justifyContent: "flex-end" }}>
              <GridItem xs={12} sx={{ justifyContent: "center" }}>
                <form onSubmit={handleCompletePressed}>
                  <Button
                    disabled={disabled}
                    color="rose"
                    type="submit"
                    value="Submit"
                  >
                    SET AS COMPLETE
                  </Button>
                </form>
              </GridItem>
            </GridContainer>
          </CardBody>
        </Card>
      </GridItem>
    </GridContainer>
  );
};

export default CompleteMerchantOnlineBooking;

CompleteMerchantOnlineBooking.propTypes = {
  history: PropTypes.object
};
